/**
 * Demo mode — a public showcase of Creator Telegrams with no real creator.
 *
 * Built with `npm run build:demo` (vite --mode demo → frontend/.env.demo).
 * No real photos, names or links, and no calls to the production API.
 * Two fictional personas, switched with `?persona=mujer|hombre`; the first one
 * shown is guessed from the Telegram account and confirmed in the onboarding.
 */
import type { AppConfig } from '@/hooks/useAppConfig'
import type { DemoContent, DemoCustomization } from './customizer'
import { SOCIAL_PLATFORMS, socialUrl } from './socials'

export const IS_DEMO = import.meta.env.VITE_DEMO_MODE === 'true'

export type PersonaId = 'mujer' | 'hombre'

export interface Persona {
  id: PersonaId
  label: string
  name: string
  handle: string
  bio: string
  aboutBio: string
}

export const PERSONAS: Record<PersonaId, Persona> = {
  mujer: {
    id: 'mujer',
    label: 'Mujer',
    name: 'Valentina Ríos',
    handle: '@valentina_demo',
    bio: 'Lifestyle, rutinas y contenido exclusivo. Este es un perfil de demostración.',
    aboutBio: 'Creadora de contenido ficticia para esta demo. Aquí verías la presentación real de cada creadora: quién es, qué publica y por qué unirse a su canal VIP.',
  },
  hombre: {
    id: 'hombre',
    label: 'Hombre',
    name: 'Mateo Cruz',
    handle: '@mateo_demo',
    bio: 'Fitness, viajes y contenido exclusivo. Este es un perfil de demostración.',
    aboutBio: 'Creador de contenido ficticio para esta demo. Aquí verías la presentación real de cada creador: quién es, qué publica y por qué unirse a su canal VIP.',
  },
}

const PERSONA_KEY = 'demo_persona'
/** The version the visitor picked (onboarding or the top switch): survives closing the Mini App */
const PERSONA_CHOICE_KEY = 'demo_persona_choice'

const isPersona = (v: unknown): v is PersonaId => v === 'mujer' || v === 'hombre'

// ─── Gender guess from the Telegram account ─────────────────────────────────
// Telegram never shares gender, so the first name is the only hint. The guess
// only picks which demo opens first: the onboarding asks to confirm it and the
// top switch changes it at any time.
const FEMALE_NAMES = new Set(('maria mariana valentina camila daniela sofia isabella isabel valeria natalia paula laura carolina ' +
  'juliana manuela sara salome luisa andrea alejandra gabriela catalina ana diana melissa paola tatiana viviana lina ' +
  'monica sandra claudia patricia adriana angela lucia martina emma mia luna antonella victoria ximena jimena karen ' +
  'katherine kelly jennifer jessica stephanie vanessa veronica lorena liliana marcela johana yuliana dayana nathaly ' +
  'nicole michelle alexa ariana briana genesis allison ashley sharon yesenia rocio consuelo amparo pilar mercedes ' +
  'carmen dolores raquel ruth esther beatriz ines isis abigail noemi belen nieves soledad luz rosario guadalupe ' +
  'mary sarah emily olivia ava charlotte amelia harper evelyn grace chloe lily zoe hannah anna elena irene alba ' +
  'noelia nerea ainhoa lucia julia marta eva clara cristina silvia rosa teresa yolanda gloria margarita estefania ' +
  'stefany yurany leidy deisy heidy wendy sindy cindy jenny lady marisol maribel ingrid astrid miriam judith').split(' '))
const MALE_NAMES = new Set(('juan jose carlos luis andres felipe santiago sebastian mateo matias nicolas daniel david alejandro ' +
  'diego miguel angel jorge javier fernando ricardo eduardo oscar camilo julian esteban cristian christian kevin ' +
  'brayan bryan jhon john jhonatan jonathan alexander alex samuel gabriel emmanuel manuel rafael raul ruben ivan ' +
  'hector victor hugo mario pablo pedro antonio francisco martin tomas simon joaquin emiliano maximiliano benjamin ' +
  'agustin lucas thiago dylan liam noah ethan james michael robert william richard joseph thomas charles mark ' +
  'steven paul brian george edward peter jack harry oliver leo enrique guillermo gustavo alberto arturo ernesto ' +
  'gerardo rodrigo sergio mauricio wilson edwin nelson jair yeison jeison stiven estiven duvan elkin ferney ' +
  'harold anderson jefferson jhonny johnny freddy frank alvaro adrian ismael elias isaac jesus ezequiel luca nikola ' +
  'joshua jonas andre josue moises saul joel abel axel bruno marco marcos dario elian leonardo orlando octavio').split(' '))
const FEMALE_ENDING_O = new Set(['rocio', 'consuelo', 'amparo', 'socorro', 'rosario'])
const MALE_ENDING_A = new Set(['luca', 'nikola', 'nicola', 'joshua', 'garcia', 'borja', 'jona', 'elia', 'mustafa', 'akira'])

/** Best guess of which demo fits a Telegram first name; null when the name says nothing. */
export function guessPersonaFromName(firstName?: string): PersonaId | null {
  const word = (firstName || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().match(/[a-z]+/)?.[0]
  if (!word || word.length < 2) return null
  if (FEMALE_NAMES.has(word)) return 'mujer'
  if (MALE_NAMES.has(word)) return 'hombre'
  if (word.endsWith('a')) return MALE_ENDING_A.has(word) ? 'hombre' : 'mujer'
  if (word.endsWith('o')) return FEMALE_ENDING_O.has(word) ? 'mujer' : 'hombre'
  return null
}

export type PersonaSource = 'url' | 'choice' | 'telegram' | 'default'

/** Where the opening persona came from: URL / this tab → the visitor's choice → the Telegram account → Mujer. */
export function resolvePersona(): { id: PersonaId; source: PersonaSource } {
  if (typeof window === 'undefined') return { id: 'mujer', source: 'default' }
  const fromUrl = new URLSearchParams(window.location.search).get('persona')
  if (isPersona(fromUrl)) {
    try { sessionStorage.setItem(PERSONA_KEY, fromUrl) } catch { /* private mode */ }
    return { id: fromUrl, source: 'url' }
  }
  let session: string | null = null
  let choice: string | null = null
  try { session = sessionStorage.getItem(PERSONA_KEY) } catch { /* private mode */ }
  try { choice = localStorage.getItem(PERSONA_CHOICE_KEY) } catch { /* private mode */ }
  if (isPersona(session)) return { id: session, source: choice === session ? 'choice' : 'url' }
  if (isPersona(choice)) return { id: choice, source: 'choice' }
  const tg = window.Telegram?.WebApp?.initDataUnsafe
  // A bot link can force it: t.me/<bot>/<app>?startapp=hombre
  const start = tg?.start_param?.toLowerCase() ?? ''
  if (start.includes('hombre')) return { id: 'hombre', source: 'url' }
  if (start.includes('mujer')) return { id: 'mujer', source: 'url' }
  const guess = guessPersonaFromName(tg?.user?.first_name)
  if (guess) return { id: guess, source: 'telegram' }
  return { id: 'mujer', source: 'default' }
}

export function getPersona(): Persona {
  return PERSONAS[resolvePersona().id]
}

/** Remembers the visitor's pick so the next visit opens the same version. */
export function rememberPersona(id: PersonaId) {
  try { localStorage.setItem(PERSONA_CHOICE_KEY, id) } catch { /* private mode */ }
}

/** Switches persona by reloading with ?persona= (keeps skin/bg/etc. in the URL). */
export function switchPersona(id: PersonaId) {
  rememberPersona(id)
  const url = new URL(window.location.href)
  url.searchParams.set('persona', id)
  window.location.assign(url.toString())
}

/**
 * Each persona's base look, layered under the visitor's own customization.
 * Mujer keeps the build's look (.env.demo: Blush + pink/blue blobs); Hombre
 * goes dark: Carbon skin, square corners, Orion.
 */
export const PERSONA_LOOK: Record<PersonaId, DemoCustomization> = {
  mujer: {},
  hombre: { skin: 'carbon', radius: 'square', constellation: 'orion' },
}

/** Visitor overrides on top of the persona's look; picking a skin or preset hands the whole look to the visitor. */
export function withPersonaLook(c: DemoCustomization): DemoCustomization {
  if (!IS_DEMO || c.skin) return c
  const look = PERSONA_LOOK[getPersona().id]
  return Object.keys(look).length ? { ...look, ...c } : c
}

/**
 * Gallery: animal photos made with AI (GPT Image 2.5 via Higgsfield), soft and
 * pastel for her, dark and low-key for him. No people, nothing real. The
 * prompts that produced them are in frontend/demo-photos-prompts.md.
 */
export const DEMO_PHOTOS: Record<PersonaId, string[]> = {
  mujer: [1, 2, 3, 4].map((n) => `/demo-photos/mujer/0${n}.jpg`),
  hombre: [1, 2, 3, 4].map((n) => `/demo-photos/hombre/0${n}.jpg`),
}

/** Full app config for the demo: fictional profile, links to example.com (reserved domain), nothing real. */
export function getDemoConfig(base: AppConfig): AppConfig {
  const p = getPersona()
  const slug = p.handle.replace('@', '')
  return {
    ...base,
    profile: { ...base.profile, name: p.name, bio: p.bio, telegramUsername: p.handle, phone: '', avatarUrl: undefined, bannerUrl: undefined },
    photos: DEMO_PHOTOS[p.id],
    socialLinks: [
      { id: 'instagram', platform: 'Instagram', url: `https://example.com/instagram/${slug}`, enabled: true },
      { id: 'tiktok', platform: 'TikTok', url: `https://example.com/tiktok/${slug}`, enabled: true },
      { id: 'x', platform: 'X', url: `https://example.com/x/${slug}`, enabled: true },
    ],
    channel: { inviteLink: '', freeChannelInviteLink: '', price: base.pricing.vipYearly },
    sales: { payment_method: 'breb-b', payment_target: '@llave-demo' },
    contact: { telegram: p.handle, phone: '' },
    bot: { botUsername: '', agentName: p.name.split(' ')[0] },
    wishlist: { url: `https://example.com/wishlist/${slug}`, enabled: true, provider: 'throne' },
  }
}

/** Layers what the prospect typed in the customizer (name, bio, prices, networks, wishlist) over the demo profile. */
export function applyDemoContent(config: AppConfig, content?: DemoContent): AppConfig {
  if (!content) return config
  const name = content.name?.trim()
  // As soon as the prospect types one network, the demo's example links give way to theirs
  const socials = content.socials ?? {}
  const socialLinks = Object.values(socials).some((v) => v.trim())
    ? SOCIAL_PLATFORMS.flatMap((p) => {
        const url = socialUrl(p.id, socials[p.id] || '')
        return url ? [{ id: p.id, platform: p.name, url, enabled: true }] : []
      })
    : config.socialLinks
  const w = content.wishlist
  return {
    ...config,
    profile: { ...config.profile, ...(name ? { name } : {}), ...(content.bio?.trim() ? { bio: content.bio.trim() } : {}) },
    pricing: {
      ...config.pricing,
      ...(content.priceFirst ? { vipMonthly: content.priceFirst } : {}),
      ...(content.priceMonthly ? { vipYearly: content.priceMonthly } : {}),
    },
    socialLinks,
    bot: { ...config.bot, ...(name ? { agentName: name.split(' ')[0] } : {}) },
    wishlist: w
      ? {
          enabled: w.enabled ?? config.wishlist.enabled,
          provider: w.provider ?? config.wishlist.provider,
          url: w.url?.trim() ? socialUrl('web', w.url) : config.wishlist.url,
        }
      : config.wishlist,
  }
}

/** Demo copy: the creator bio is fictional ({NAME} placeholders are filled from the persona's profile). */
export function demoTranslations<T extends Record<string, string>>(t: T): T {
  return { ...t, about_bio: getPersona().aboutBio }
}
